import React, { useState } from 'react';
import { ChevronRight, Clock, Coffee, FileText, LogOut, Menu, MessageCircle, Wallet } from 'lucide-react';
import { t } from '../../i18n';
import { SIT_OUT_COPY } from '../../utils/sitOutCopy';
import Modal from '../ui/Modal';
import HandRankingsModal from '../HandRankingsModal';
import { HistoryModal } from './GameParts';

// Round ☰ in the game-screen header. Opens the shared centered Menu modal:
// view switch (Table | List) then Balances / History / Hands / Feedback /
// Leave game. Each row opens the existing feature. Layout is still a
// per-device preference — never room state.

const ROW =
  'w-full flex items-center gap-3 min-h-[52px] text-left bg-transparent border-0 px-0 text-ink';

function MenuRow({ icon: Icon, label, testId, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ WebkitTapHighlightColor: 'transparent' }}
      className={ROW}
      data-testid={testId}
    >
      <Icon className="w-[18px] h-[18px] text-ink flex-shrink-0" strokeWidth={1.75} aria-hidden />
      <span className="flex-1 text-[16px] font-medium tracking-[-0.01em]">{label}</span>
      <ChevronRight className="w-5 h-5 text-mute flex-shrink-0" aria-hidden />
    </button>
  );
}

export default function GameMenu({
  layout,
  onToggleLayout,
  onOpenBalances,
  onOpenFeedback,
  onLeaveTable,
  onSitOut,
  roundActions,
}) {
  const [open, setOpen] = useState(false);
  const [handsOpen, setHandsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const isTable = layout === 'table';

  const closeAnd = (fn) => () => {
    setOpen(false);
    fn?.();
  };

  const seg = (active) =>
    `flex-1 h-11 rounded-full text-[15px] font-medium transition-colors ${
      active ? 'bg-ink text-cream' : 'bg-transparent text-mute'
    }`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('common.menu')}
        style={{ WebkitTapHighlightColor: 'transparent' }}
        className="w-9 h-9 rounded-full border border-line text-ink flex items-center justify-center hover:border-ink transition-colors flex-shrink-0"
        data-testid="game-menu-button"
      >
        <Menu className="w-4 h-4" strokeWidth={2} aria-hidden />
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('common.menu')}
        testId="game-menu"
      >
        <div className="label-eyebrow text-mute mb-2">{t('game.view')}</div>
        <div
          role="group"
          aria-label={t('game.layout')}
          className="flex rounded-full bg-cream-2 p-[3px] mb-4"
          data-testid="menu-view-switch"
        >
          <button
            type="button"
            aria-pressed={isTable}
            aria-label={t('game.tableLayout')}
            onClick={() => onToggleLayout?.('table')}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className={seg(isTable)}
            data-testid="menu-view-table"
          >
            {t('game.table')}
          </button>
          <button
            type="button"
            aria-pressed={!isTable}
            aria-label={t('game.listLayout')}
            onClick={() => onToggleLayout?.('list')}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className={seg(!isTable)}
            data-testid="menu-view-list"
          >
            {t('game.list')}
          </button>
        </div>

        <div className="divide-y divide-line -mx-5 px-5">
          <MenuRow
            icon={Wallet}
            label={t('game.balances')}
            testId="menu-balances"
            onClick={closeAnd(onOpenBalances)}
          />
          <MenuRow
            icon={Clock}
            label={t('game.history')}
            testId="menu-history"
            onClick={() => {
              setOpen(false);
              setHistoryOpen(true);
            }}
          />
          <MenuRow
            icon={FileText}
            label={t('game.hands')}
            testId="hands-pill"
            onClick={() => {
              setOpen(false);
              setHandsOpen(true);
            }}
          />
          <MenuRow
            icon={MessageCircle}
            label={t('game.feedback')}
            testId="feedback-pill"
            onClick={closeAnd(onOpenFeedback)}
          />
          {onSitOut && (
            <MenuRow
              icon={Coffee}
              label={SIT_OUT_COPY.menuRow}
              testId="sit-out-pill"
              onClick={closeAnd(onSitOut)}
            />
          )}
          {onLeaveTable && (
            <MenuRow
              icon={LogOut}
              label={t('game.leaveGame')}
              testId="leave-pill"
              onClick={closeAnd(onLeaveTable)}
            />
          )}
        </div>
      </Modal>

      <HistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        actions={roundActions}
      />
      <HandRankingsModal open={handsOpen} onClose={() => setHandsOpen(false)} />
    </>
  );
}
